import API from 'utils/api';
import { store } from 'store';
import { onError, onSuccess } from 'utils/handlers';
import getFormattedValues, { hasFiles } from 'utils/formatValues';
import {
  SET_ESTADOS_CIVILES,
  SET_ESTADOS_DE_LOS_FUNCIONARIOS,
  SET_GENEROS,
  SET_LADAS,
  SET_TIPOS_VIALIDADES,
} from 'store/reducers/catalogs';
import {
  UPDATE_ENTIDAD_INFO,
  UPDATE_USER_INFO,
  SET_PERIODO_FISCAL_SELECTED,
} from 'store/reducers/auth';
import { SET_CORTE } from 'store/reducers/app';
import { getUnidadDeRecaudacion } from 'api/configuracion/unidadesDeRecaudacion';

export const baseURI = 'usuarios/';

/**
 * GET Informacion de Usuario
 * @returns {Promise<Object>}
 */
export const getCurrentUser = async () => {
  try {
    const response = await API.get(`${baseURI}id/`);
    const { data } = response;
    const { roles } = data;
    delete data?.roles;
    store.dispatch({
      type: UPDATE_USER_INFO,
      payload: {
        user: data,
        roles,
      },
    });
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * PATCH Informacion de Usuario
 * @param {string} values user values
 * @returns {Promise<object>}
 */
export const patchCurrentUser = async (values) => {
  try {
    const files = ['foto'];
    const config = hasFiles(values, files) ? {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    } : {};
    const formattedValues = getFormattedValues(values, {
      files,
      formData: config.headers,
      clean: false,
    });
    const response = await API.patch(`${baseURI}id/`, formattedValues, config);
    onSuccess(response, 'Actualizado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Estados del Funcionario
 * @returns {Promise<Array<object>>}
 */
export const getEstadosDelFuncionario = async () => {
  try {
    if (!store.getState().catalogs.estadosDeLosFuncionarios?.length) {
      const response = await API.get(`${baseURI}estados-del-funcionario`);
      store.dispatch({
        type: SET_ESTADOS_DE_LOS_FUNCIONARIOS,
        payload: response.data,
      });
      return response.data;
    }
    return store.getState().catalogs.estadosDeLosFuncionarios;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Estados Civiles
 * @returns {Promise<Array<object>>}
 */
export const getEstadosCiviles = async () => {
  try {
    if (!store.getState().catalogs.estadosCiviles?.length) {
      const response = await API.get(`${baseURI}estados-civiles`);
      store.dispatch({
        type: SET_ESTADOS_CIVILES,
        payload: response.data,
      });
      return response.data;
    }
    return store.getState().catalogs.estadosCiviles;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Géneros
 * @returns {Promise<Array<object>>}
 */
export const getGeneros = async () => {
  try {
    if (!store.getState().catalogs.generos?.length) {
      const response = await API.get(`${baseURI}generos`);
      store.dispatch({
        type: SET_GENEROS,
        payload: response.data,
      });
      return response.data;
    }
    return store.getState().catalogs.generos;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Ladas
 * @returns {Promise<Array<object>>}
 */
export const getLadas = async () => {
  try {
    if (!store.getState().catalogs.ladas?.length) {
      const response = await API.get(`${baseURI}lada`);
      store.dispatch({
        type: SET_LADAS,
        payload: response.data,
      });
      return response.data;
    }
    return store.getState().catalogs.ladas;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Tipos de Vialidades
 * @returns {Promise<Array<object>>}
 */
export const getTiposDeVialidades = async (params) => {
  try {
    if (!params) {
      const tiposVialidades = store.getState().catalogs?.tiposVialidades;
      if (tiposVialidades?.length) return tiposVialidades;
    }
    const response = await API.get(`${baseURI}tipo-vialidad`);
    if (!params) store.dispatch({ type: SET_TIPOS_VIALIDADES, payload: response.data });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Datos del usuario
 * @returns {Promise<Object|boolean>}
 */
export const getUserData = async () => {
  try {
    const { data } = await API.get('usuarios/id/');
    if (data) {
      const {
        entidad,
        corte,
        roles,
        ...user
      } = data;
      if (user.unidad_de_recaudacion) {
        user.unidad_de_recaudacion_index = await getUnidadDeRecaudacion(
          user.unidad_de_recaudacion,
        );
      }
      store.dispatch({
        type: UPDATE_USER_INFO,
        payload: {
          user,
          roles,
        },
      });
      store.dispatch({
        type: UPDATE_ENTIDAD_INFO,
        payload: entidad,
      });
      store.dispatch({
        type: SET_CORTE,
        payload: corte,
      });
      store.dispatch({
        type: SET_PERIODO_FISCAL_SELECTED,
        payload: entidad.periodo_fiscal_vigente,
      });
    }
    return data;
  } catch (err) {
    onError(err);
    return false;
  }
};

/**
 * PUT Change Password
 * @param {object} values
 * @returns {Promise<Array|boolean>}
 */
export const changePassword = async (values) => {
  try {
    const response = await API.put('usuarios/actualizar-contrasena', values);
    onSuccess(response, 'Contraseña Actualizada Correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};
